<template>
  <div>
    <h1 class="settings-title">
      Automation
    </h1>
    <div
      class="settings-form">
      <h2 class="settings-subtitle">
        Instant Replies
      </h2>
      <b-form-group>
        <label class="settings-label">
          <span>Default Reply Delay</span>
          <icon
            id="default-reply-description"
            v-b-tooltip="getTooltipConfig('Select after what period of time you want to send the Default Reply message again to returning WhatsApp user.')"
            name="info-circle"
            class="info-label"/>
        </label>
        <b-select
          v-model="defaultReplyDelay"
          :plain="true"
          :options="delays"
          class="settings-select mb-2"/>
      </b-form-group>
      <b-form-group>
        <label class="settings-label">
          <span>Away Reply Delay</span>
          <icon
            id="away-reply-description"
            v-b-tooltip="getTooltipConfig('Select after what period of time you want to send the Away Reply message again to returning WhatsApp user.')"
            name="info-circle"
            class="info-label"/>
        </label>
        <b-select
          v-model="awayReplyDelay"
          :plain="true"
          :options="delays"
          class="settings-select mb-2"/>
      </b-form-group>
      <b-form-group>
        <label class="settings-label">
          <span>Automated Reply Sequence Duration</span>
          <icon
            id="sequence-description"
            v-b-tooltip="getTooltipConfig('Select for how long WhatsApp user would be able to continue with the instant reply sequence if they left the conversation.')"
            name="info-circle"
            class="info-label"/>
        </label>
        <b-select
          v-model="sequenceDelay"
          :plain="true"
          :options="delays"
          class="settings-select mb-2"/>
      </b-form-group>
      <b-form-group>
        <label class="settings-label">
          <span>Human Takeover Duration</span>
          <icon
            id="in-touch-description"
            v-b-tooltip="getTooltipConfig('Select for how long automated replies should be turned off from the moment an agent sent a message to a WhatsApp user.')"
            name="info-circle"
            class="info-label"/>
        </label>
        <b-select
          v-model="inTouchDuration"
          :plain="true"
          :options="delays"
          class="settings-select mb-2"/>
      </b-form-group>
      <b-form-group>
        <label class="settings-label">
          <span>Stop Keyword</span>
          <icon
            id="stop-word-description"
            v-b-tooltip="getTooltipConfig('Keyword that WhatsApp user can send to stop the automated reply sequence.')"
            name="info-circle"
            class="info-label"/>
        </label>
        <b-input
          v-model="stopWord"
          v-validate="'required'"
          name="stop_word"
          data-vv-as="Stop Word"
          type="text"/>
        <span
          v-if="errors.has('stop_word')"
          class="validation-error">{{ errors.first('stop_word') }}</span>
        <span
          v-if="validationErrors.stopWord"
          class="validation-error">{{ validationErrors.stopWord }}</span>
      </b-form-group>
      <div class="clearfix"/>
      <div
        v-if="settingsSaved"
        class="success-info">
        Automation settings have been successfully updated.
      </div>
      <div
        v-if="errorMessage"
        class="error-message">
        {{ errorMessage }}
      </div>
      <b-button
        :class="{loading: saving}"
        :disabled="saving"
        class="btn-round btn-save"
        @click="updateAutomationSettings">
        <span v-if="!saving">Save</span>
        <div class="loader"><div class="loading-circle"/></div>
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import setValidationErrors from '@/helpers/validationErrors';

export default {
  name: 'AutomationSettings',
  data() {
    return {
      defaultReplyDelay: '1hour',
      awayReplyDelay: '1hour',
      sequenceDelay: '1hour',
      inTouchDuration: '1hour',
      delays: {
        '1hour': '1 hour',
        '12hours': '12 hours',
        '1day': '1 day',
        '1week': '1 week',
      },
      stopWord: '',
      settingsSaved: false,
      saving: false,
      errorMessage: '',
      validationErrors: {},
    };
  },
  computed: {
    ...mapGetters({
      automationSettings: 'automation/automationSettings',
      automationSettingsSaved: 'automation/automationSettingsSaved',
      apiError: 'automation/apiError',
    }),
  },
  watch: {
    automationSettingsSaved(saved) {
      if (saved) {
        this.saving = false;
        this.settingsSaved = true;
        setTimeout(() => {
          this.settingsSaved = false;
        }, 3000);
      }
    },
    automationSettings(settings) {
      this.defaultReplyDelay = settings.default_message_delay;
      this.sequenceDelay = settings.message_sequence_duration;
      this.inTouchDuration = settings.in_touch_duration;
      this.stopWord = settings.stop_word;
    },
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.validationErrors = setValidationErrors(error.data.errors);
      } else {
        this.errorMessage = error.data.message;
      }
    },
  },
  mounted() {
    this.fetchSettings(this.$route.params.merchant_id);
  },
  methods: {
    ...mapActions({
      fetchSettings: 'automation/fetchSettings',
      updateSettings: 'automation/updateSettings',
    }),
    getTooltipConfig(text) {
      return {
        title: text,
        placement: 'right',
        animation: false,
        offset: '0, 5px',
      };
    },
    updateAutomationSettings() {
      this.validationErrors = {};
      this.$validator.validate().then((valid) => {
        if (valid) {
          this.saving = true;
          const payload = {
            merchant_id: this.$route.params.merchant_id,
            data: {
              default_message_delay: this.defaultReplyDelay,
              away_message_delay: this.awayReplyDelay,
              message_sequence_duration: this.sequenceDelay,
              in_touch_duration: this.inTouchDuration,
              stop_word: this.stopWord,
            },
          };

          this.updateSettings(payload);
        }
      });
    },
  },
};
</script>
